import { NavLink } from 'react-router-dom';
import {
  BookOpenText,
  Map,
  Sparkles,
  ScrollText,
  Wand2,
  UserRound,
  Home,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMetaStore } from '@/engine/state';
import { cn } from '@/utils/cn';

const ITEMS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/game/story', label: 'Historia', icon: BookOpenText },
  { to: '/game/map', label: 'Mapa', icon: Map },
  { to: '/game/skills', label: 'Skills', icon: Sparkles },
  { to: '/game/log', label: 'Log', icon: ScrollText },
  { to: '/game/spells', label: 'Hechizos', icon: Wand2 },
  { to: '/game/factions', label: 'Facciones', icon: Users },
  { to: '/game/character', label: 'Personaje', icon: UserRound },
];

export function NavigationRail() {
  const combateActivo = useMetaStore((s) => s.combateActivo);

  // Durante un combate, bloqueamos toda la navegación para que el jugador
  // no pueda "escapar" del pleito abriendo otro panel y volviendo: al
  // desmontar la pantalla de combate se perdería el estado y la pelea
  // se reiniciaría con la vida que ya había perdido.
  const bloqueado = combateActivo;

  return (
    <nav
      aria-label="Navegación principal"
      aria-disabled={bloqueado}
      className={cn(
        'order-last md:order-first',
        'shrink-0 w-full md:w-16 xl:w-52',
        'h-14 md:h-full',
        'flex md:flex-col',
        'border-t md:border-t-0 md:border-r border-ash-800/60',
        'bg-ash-950/90 backdrop-blur-sm',
        'overflow-x-auto md:overflow-visible',
        bloqueado && 'pointer-events-none select-none opacity-40',
      )}
    >
      <NavLink
        to="/menu"
        tabIndex={bloqueado ? -1 : undefined}
        onClick={(e) => bloqueado && e.preventDefault()}
        className="hidden md:flex items-center gap-3 px-3 xl:px-4 py-3 text-ash-400 hover:text-ember-300 transition-colors"
        title={bloqueado ? 'No podés salir durante un combate' : 'Menú principal'}
      >
        <Home className="w-5 h-5 shrink-0" />
        <span className="hidden xl:inline font-display tracking-widest text-xs uppercase">
          Menú
        </span>
      </NavLink>
      <div className="hidden md:block h-px bg-ash-800/60 mx-3 my-2" />

      <ul className="flex md:flex-col w-full md:w-auto px-2 md:px-1 xl:px-2 gap-0.5 md:gap-1 items-center md:items-stretch justify-between md:justify-start">
        {ITEMS.map(({ to, label, icon: Icon }) => (
          <li key={to} className="flex-1 md:flex-none">
            <NavLink
              to={to}
              tabIndex={bloqueado ? -1 : undefined}
              onClick={(e) => bloqueado && e.preventDefault()}
              className={({ isActive }) =>
                cn(
                  'flex md:items-center justify-center md:justify-start flex-col md:flex-row',
                  'gap-0.5 md:gap-3',
                  'px-1 md:px-3 py-1.5 md:py-2.5',
                  'rounded-md font-ui text-[10px] md:text-sm transition-colors',
                  isActive
                    ? 'text-moss-300 md:bg-moss-900/60'
                    : 'text-ash-400 hover:text-ash-100 md:hover:bg-ash-900/40',
                )
              }
              title={bloqueado ? 'Bloqueado durante el combate' : label}
            >
              <Icon className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
              <span className="md:hidden xl:inline leading-none">{label}</span>
              <span className="hidden md:inline xl:hidden sr-only">{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
